import React from "react";
import { graphql } from "gatsby";
import { GatsbyImage, getImage, getSrc } from "gatsby-plugin-image";
import * as c from "./blogPost.module.scss";
import SEO from "@components/seo.js";
import {
  BlogLayout,
  BlogPostBody,
  BlogPostGrid,
  AuthorPhotoNameTitle,
  AuthorPhotoNameByline,
  CategoryButtons,
} from "@components/blog/";
import { getBlogPostingSchema } from "@components/blog/blogSchema";

export default function BlogPost({ data }) {
  const { contentfulBlogPost: post } = data;

  if (
    !post?.title ||
    !post?.publishedAt ||
    !post?.author?.name ||
    !post?.subhead?.subhead ||
    !post?.body ||
    !post?.featureImage ||
    !post?.author
  ) {
    return <h1>Post can't be previewed</h1>;
  }

  let metaDescription;
  if (post?.metaDescription?.metaDescription) {
    metaDescription = post.metaDescription.metaDescription;
  }

  let meta = [];

  if (post?.featureImage?.social) {
    const socialImageUrl = getSrc(post.featureImage.social);

    if (socialImageUrl) {
      const { width, height } = getImage(post.featureImage.social);

      meta.push(
        ...[
          {
            property: "og:image",
            content: socialImageUrl,
          },
          {
            property: "og:image:secure_url",
            content: socialImageUrl,
          },
          {
            property: "og:image:width",
            content: width,
          },
          {
            property: "og:image:height",
            content: height,
          },
        ]
      );

      if (post.featureImage.mimeType) {
        meta.push({
          property: "og:image:type",
          content: post.featureImage.mimeType,
        });
      }
    }
  }

  const { Section, Header } = BlogLayout;

  const AuthorPhotoNameContent = <AuthorPhotoNameTitle author={post.author} />;
  const AuthorPhotoNameBylineContent = <AuthorPhotoNameByline className={c.author} author={post.author} />;

  return (
    <BlogLayout className={c.container}>
      <SEO title={post.title} description={metaDescription} meta={meta} />
      <Header className={c.header}>
        <Header.Breadcrumbs />

        <Header.Headline label={post.title} useTitleCase={false} />
        <p className={c.subhead}>{post.subhead.subhead}</p>

        <GatsbyImage className={c.featureImage} loading="eager" image={getImage(post.featureImage)} alt={post.title} />

        {AuthorPhotoNameContent}

        {post?.categories?.length > 0 && (
          <CategoryButtons>
            {post.categories.map((category) => (
              <CategoryButtons.Button
                label={category.name}
                key={category.id}
                selected={false}
                linkTo={`/blog/c/${category.name.toLowerCase()}`}
              />
            ))}
          </CategoryButtons>
        )}
      </Header>
      <Section className={c.body}>
        <time className={c.publishedAt} pubdate="pubdate" dateTime={post.publishedAt}>
          {post.publishedAt}
        </time>
        <BlogPostBody body={post.body} businessTypes={post?.businessTypes} />
      </Section>

      {AuthorPhotoNameBylineContent}

      {post?.relatedPosts?.length > 0 && (
        <Section className={c.section}>
          <Section.Headline label="Related articles" />
          <BlogPostGrid items={post.relatedPosts} />
        </Section>
      )}
    </BlogLayout>
  );
}

export const query = graphql`
  query ($slug: String) {
    contentfulBlogPost(
      slug: { eq: $slug }
      title: { ne: null }
      publishedAt: { ne: null }
      author: { name: { ne: null } }
      subhead: { subhead: { ne: null } }
    ) {
      category
      categories {
        id
        name
        slug
        path: gatsbyPath(filePath: "/blog/c/{mangomintBlogPostCategory.name}")
      }
      articlePath: gatsbyPath(filePath: "/blog/{contentfulBlogPost.slug}")
      title
      metaDescription {
        metaDescription
      }
      publishedAt(formatString: "MMMM D, YYYY")
      author {
        name
        title
        activeProfile
        biography {
          raw
        }
        photo {
          gatsbyImageData(width: 120, quality: 90, layout: CONSTRAINED)
        }
        byline {
          byline
        }
      }
      featureImage {
        gatsbyImageData(width: 760, quality: 90, layout: CONSTRAINED)
        social: gatsbyImageData(width: 1600, quality: 90, layout: CONSTRAINED)
        mimeType
      }
      subhead {
        subhead
      }
      body {
        raw
        references {
          __typename
          ... on Node {
            ... on ContentfulLandingPageSectionEmailCapture {
              contentful_id
              id
              heading
              buttonLabel
              emailCapture {
                __typename
                ... on ContentfulCtaEmailCapture {
                  variant
                  inputPlaceholder
                  buttonText
                  successMessage
                }
              }
              subheading {
                subheading
              }
              leftImage {
                gatsbyImageData(width: 320, quality: 90, layout: CONSTRAINED)
              }
              rightImage {
                gatsbyImageData(width: 320, quality: 90, layout: CONSTRAINED)
              }
              logo {
                gatsbyImageData(width: 160, quality: 90, layout: CONSTRAINED)
              }
            }
            ... on ContentfulAsset {
              contentful_id
              # contentful_id is required to resolve the references

              gatsbyImageData(width: 640, quality: 90, layout: CONSTRAINED)

              file {
                contentType
                url
              }
            }
            ... on ContentfulBlogPost {
              contentful_id
              # contentful_id is required to resolve the references

              slug
            }
            ... on ContentfulBlogPostHtmlEmbed {
              contentful_id
              # contentful_id is required to resolve the references

              html {
                html
              }
            }
            ... on ContentfulBlogPostWistiaVideoEmbed {
              contentful_id
              # contentful_id is required to resolve the references

              title
              wistiaVideoId
            }
            ... on ContentfulBlogPostInternalAd {
              contentful_id
              # contentful_id is required to resolve the references

              heading
              subheading {
                subheading
              }
              image {
                gatsbyImageData(height: 256, quality: 90, layout: CONSTRAINED)
              }
              ctas {
                __typename
                ... on ContentfulCtaButton {
                  id
                  label
                  url
                  isExternal
                  trackedEventName
                  openChat
                }
              }
            }
          }
        }
      }
      relatedPosts {
        ...blogPostCardFields
      }
      businessTypes
    }
  }
`;

export function Head({ data }) {
  const post = data?.contentfulBlogPost;
  if (!post) return null;
  const blogPostingSchema = getBlogPostingSchema({
    post,
    logoUrl: "/images/mangomint-logo.png",
    publisherUrl: "https://www.mangomint.com/",
  });
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(blogPostingSchema),
        }}
      />
    </>
  );
}
